import { useSyncExternalStore } from 'react'

/**
 * Same breakpoint as Tailwind’s default `xl` (1280px). Used so JS can choose
 * layout (e.g. table vs card) in one place instead of duplicating responsive
 * CSS that still mounted both branches.
 */
const XL_MIN_WIDTH_MEDIA_QUERY = '(min-width: 1280px)'

/**
 * Registers a listener on the media query. React calls this once on mount;
 * when the viewport crosses 1280px, `onChange` runs and React re-reads the snapshot.
 */
function subscribe(onChange: () => void) {
  const mq = window.matchMedia(XL_MIN_WIDTH_MEDIA_QUERY)
  mq.addEventListener('change', onChange)
  return () => mq.removeEventListener('change', onChange)
}

/**
 * Current value: whether the viewport matches “min-width 1280px”. Called on
 * every render and after subscribe fires so the hook stays in sync with the window.
 * When `window` is missing (SSR/tests), returns `false` instead of touching `matchMedia`.
 */
function getSnapshot() {
  if (typeof window === 'undefined') {
    return false
  }
  return window.matchMedia(XL_MIN_WIDTH_MEDIA_QUERY).matches
}

/**
 * `true` when the viewport is at least `xl` (1280px). Implemented with
 * `useSyncExternalStore` so the subscription to `matchMedia` is correct under
 * React 18+ concurrent rendering (unlike a naive `useState` + `useEffect`).
 */
export function useIsXlUp() {
  return useSyncExternalStore(subscribe, getSnapshot)
}
